import { useEffect, useRef, useSyncExternalStore } from 'react';
import { THEMES } from '../ui/tokens';
import { resolveTheme, type Theme, type ThemePreference } from './theme';

/**
 * The theme preference (Ruling 11 of P11): stored on this device only, `light` by default (P5e: the editor is light
 * unless the person chose otherwise), shared by every
 * `useTheme` through one module-level store so Settings and the root applier never disagree.
 */
export const THEME_KEY = 'os.theme';
/** The browser bar (<meta name=theme-color>) per theme; public/theme-init.js sets the same values before the first paint. */
export const themeColor = (theme: Theme): string => THEMES[theme].bg;
export const DEFAULT_THEME_COLOR = themeColor('light');
const PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'];

/** The choice of this session when the storage refuses writes (private browsing, quota). */
let fallback: ThemePreference | null = null;
const listeners = new Set<() => void>();

export function readThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored !== null) return PREFERENCES.includes(stored as ThemePreference) ? (stored as ThemePreference) : 'light';
  } catch {
    // Blocked storage: the session's choice, or light.
  }
  return fallback ?? 'light';
}

export function writeThemePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_KEY, preference);
    fallback = null;
  } catch {
    fallback = preference;
  }
  listeners.forEach((listener) => listener());
}

const darkQuery = () => (typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null);

/** Without matchMedia (tests, old engines) the app keeps the tokens' default, dark (spec §14). */
export const systemPrefersDark = (): boolean => darkQuery()?.matches ?? true;

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const media = darkQuery();
  const onStorage = (e: StorageEvent) => {
    if (e.key === THEME_KEY || e.key === null) listener();
  };
  media?.addEventListener?.('change', listener);
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    media?.removeEventListener?.('change', listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** The preference and its setter; applies the resolved theme as `data-theme` on <html>. */
export function useTheme(): [ThemePreference, (preference: ThemePreference) => void] {
  const preference = useSyncExternalStore(subscribe, readThemePreference);
  const dark = useSyncExternalStore(subscribe, systemPrefersDark);
  const theme = resolveTheme(preference, dark);
  const applied = useRef<string | null>(null);
  useEffect(() => {
    const root = document.documentElement;
    const switching = applied.current !== null && root.dataset.theme !== theme;
    root.dataset.theme = theme;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', themeColor(theme));
    applied.current = theme;
    if (!switching) return;
    // P15c: every control eases its colours (.os-press); a theme switch would fade them all at once. Two frames
    // without transitions (globals.css) let the new colours land at once.
    root.dataset.themeSwitching = '';
    requestAnimationFrame(() => requestAnimationFrame(() => delete root.dataset.themeSwitching));
  }, [theme]);
  return [preference, writeThemePreference];
}

/** Applies the theme from the root of the app (every route but the design gallery, which sets its own). */
export function ThemeApplier(): null {
  useTheme();
  return null;
}
